/**
 * One progress bar for every scene that loads art.
 *
 * Preload always loaded behind a bar, but since art moved to loading on demand
 * (Prompt 10) Game and Title fetch files in their own preload() - and they had
 * no bar at all, so a level on a slow connection opened on a blank screen.
 */
import Phaser from 'phaser';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, PALETTE } from '../../types';

/**
 * How long a load must still be running before the bar appears.
 *
 * Most levels find their art already cached and load in a frame or two; a bar
 * that flashed up and vanished before every level would read as a glitch.
 */
export const LOADING_REVEAL_MS = 180;

const BAR_W = 520;
const BAR_H = 28;
const INSET = 4;

/**
 * Show a bar for whatever is queued on this scene's loader.
 *
 * Call from preload(), after queueing. Does nothing when nothing was queued -
 * Phaser then skips straight to create() and never fires `complete`, which
 * would leave a bar on screen for good.
 */
export function showLoadingBar(
  scene: Phaser.Scene,
  { revealMs = LOADING_REVEAL_MS, label = 'Loading...' } = {},
): void {
  if (scene.load.list.size === 0) return;

  let done = false;
  let progress = 0;
  let text: Phaser.GameObjects.Text | undefined;
  let bar: Phaser.GameObjects.Graphics | undefined;

  const draw = (): void => {
    if (bar === undefined || !bar.active) return;
    const x = (GAME_WIDTH - BAR_W) / 2;
    const y = GAME_HEIGHT / 2;
    bar.clear();
    bar.lineStyle(INSET, 0x8ecae6, 1);
    bar.strokeRoundedRect(x, y, BAR_W, BAR_H, BAR_H / 2);

    const inner = BAR_H - INSET * 2;
    const fill = (BAR_W - INSET * 2) * Math.min(1, Math.max(0, progress));
    if (fill <= 0) return;
    // The corner radius may not exceed half the width: a nearly empty bar
    // with a full radius draws as a smudge rather than a short pill.
    bar.fillStyle(0x2ec4b6, 1);
    bar.fillRoundedRect(x + INSET, y + INSET, fill, inner, Math.min(inner / 2, fill / 2));
  };

  const reveal = (): void => {
    if (done || !scene.sys.isActive()) return;
    text = scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 44, label, {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1000);
    bar = scene.add.graphics().setScrollFactor(0).setDepth(1000);
    draw();
  };

  const onProgress = (value: number): void => {
    progress = value;
    draw();
  };

  scene.load.on(Phaser.Loader.Events.PROGRESS, onProgress);
  scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
    done = true;
    scene.load.off(Phaser.Loader.Events.PROGRESS, onProgress);
    text?.destroy();
    bar?.destroy();
  });

  // A plain timeout, because the scene's own clock does not run until create().
  if (revealMs <= 0) reveal();
  else window.setTimeout(reveal, revealMs);
}
